import { z } from 'zod';

export const TarificationPeriodSchema = z
  .object({
    startDate: z.date({ message: 'La date de début est requise' }),
    endDate: z.date({ message: 'La date de fin est requise' }),
    applicationDays: z
      .array(
        z.enum([
          'Lundi',
          'Mardi',
          'Mercredi',
          'Jeudi',
          'Vendredi',
          'Samedi',
          'Dimanche',
        ]),
      )
      .min(1, { message: '1 choix au minimum est requis' }),
  })
  .refine((data) => data.startDate <= data.endDate, {
    message: 'Les dates de définition de période sont invalides.',
  });

export const pricePartitionSchema = z
  .object({
    price: z.preprocess(
      (value) => (value === '' ? undefined : Number(value)),
      z
        .number({ message: 'Le tarif est requis' })
        .min(0, { message: 'Le tarif est requis' }),
    ),
    from: z.coerce
      .number({ message: 'Le nombre de jours est requis' })
      .min(1, { message: 'Le nombre de jours est requis' }),
    to: z.coerce
      .number({ message: 'Le nombre de jours est requis' })
      .min(1, { message: 'Le nombre de jours est requis' }),
    specialPrice: z.coerce.number().optional(),
  })
  .refine((data) => data.from <= data.to, {
    message: 'Une ou plusieurs répartitions sont invalides.',
  });

export const TarificationSchema = z.object({
  name: z.union([z.string(), z.null()]).optional(),
  periods: z
    .array(TarificationPeriodSchema)
    .min(1)
    .refine(
      (data) => {
        if (data.length <= 1) return true;
        for (let i = 1; i < data.length; i++)
          if (data[i].startDate <= data[i - 1].endDate) return false;
        return true;
      },
      {
        path: ['periods'],
        message: 'Les périodes ne peuvent pas se croiser',
      },
    ),
  pricePartitions: z
    .array(pricePartitionSchema)
    .min(1)
    .refine(
      (data) => {
        if (data.length <= 1) return true;
        for (let i = 1; i < data.length; i++)
          if (data[i].from <= data[i - 1].to) return false;
        return true;
      },
      {
        path: ['pricePartitions'],
        message: 'Les répartitions ne peuvent pas se croiser',
      },
    ),
});

export const ExtendedTarificationSchema = z.object({
  tarifType: z.enum(['par-prestation', 'par-personne']).optional(),
  customTarif: z.enum(['true', 'false']).optional(),
  childAgeFrom: z.coerce.number().int().min(0).optional(),
  childAgeTo: z.coerce.number().int().min(1).optional(),
  tarifs: z.array(TarificationSchema),
});

export type ExtendedTarificationFormData = z.infer<
  typeof ExtendedTarificationSchema
>;

export type TarificationFormData = z.infer<typeof TarificationSchema>;
